import { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2, ArrowLeft, Ban } from 'lucide-react';
import { useLocation } from 'wouter';

interface SubscriptionDetail {
  id: string;
  status: string;
  customerId: string;
  planId: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialStart?: string;
  trialEnd?: string;
  canceledAt?: string;
  cancelAtPeriodEnd: boolean;
  gracePeriodEndsAt?: string;
  dunningAttempts: number;
  lastDunningAttemptAt?: string;
}

export default function SubscriptionDetail() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetchSubscription();
  }, [id]);

  const fetchSubscription = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/admin/payments/subscriptions/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Failed to fetch subscription', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      const response = await fetch(`/api/admin/payments/subscriptions/${id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ atPeriodEnd: false }),
      });
      if (response.ok) {
        await fetchSubscription();
      }
    } catch (error) {
      console.error('Failed to cancel subscription', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-amber-100 text-amber-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => setLocation('/admin/payments')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <p className="mt-4 text-muted-foreground">Subscription not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setLocation('/admin/payments')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {subscription.status === 'active' && (
            <Button variant="destructive" size="sm" onClick={handleCancel} disabled={isCancelling}>
              {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
              Cancel Subscription
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Subscription Details</CardTitle>
              <Badge className={getStatusColor(subscription.status)}>{subscription.status}</Badge>
            </div>
            <CardDescription className="text-xs">ID: {subscription.id}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Customer ID</p>
                <p className="text-sm font-mono">{subscription.customerId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Plan ID</p>
                <p className="text-sm font-mono">{subscription.planId}</p>
              </div>
            </div>

            <Separator />

            {subscription.currentPeriodStart && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Period Start</p>
                  <p className="text-sm">{new Date(subscription.currentPeriodStart).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Period End</p>
                  <p className="text-sm">{new Date(subscription.currentPeriodEnd || '').toLocaleDateString()}</p>
                </div>
              </div>
            )}

            {subscription.trialEnd && (
              <div>
                <p className="text-xs text-muted-foreground">Trial Ends</p>
                <p className="text-sm">{new Date(subscription.trialEnd).toLocaleDateString()}</p>
              </div>
            )}

            {subscription.canceledAt && (
              <div>
                <p className="text-xs text-muted-foreground">Canceled At</p>
                <p className="text-sm">{new Date(subscription.canceledAt).toLocaleDateString()}</p>
              </div>
            )}

            {subscription.gracePeriodEndsAt && (
              <div>
                <p className="text-xs text-muted-foreground">Grace Period Ends</p>
                <p className="text-sm">{new Date(subscription.gracePeriodEndsAt).toLocaleDateString()}</p>
              </div>
            )}

            {subscription.dunningAttempts > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Dunning Attempts</p>
                <p className="text-sm">{subscription.dunningAttempts}</p>
              </div>
            )}

            {subscription.lastDunningAttemptAt && (
              <div>
                <p className="text-xs text-muted-foreground">Last Dunning Attempt</p>
                <p className="text-sm">{new Date(subscription.lastDunningAttemptAt).toLocaleDateString()}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
